import { useState, useCallback, useEffect, useRef } from "react";
import { useUser } from "@/hooks/useUser";
import { updateUserSettings } from "@/lib/users-api";

const TOUR_STORAGE_PREFIX = "bac.host_tour_seen.";

// Task #4221 — the host tour now powers more than the Dashboard. Each page's
// tour tracks its own "seen" state via a distinct profile timestamp column and
// a distinct localStorage key, so completing one tour never suppresses another.
//
// The Dashboard tour keeps its ORIGINAL storage key + profile field verbatim
// (`bac.host_tour_seen.<userId>` / `host_tour_seen_at`) so existing users who
// already saw it aren't shown it again.
export type HostTourProfileField =
  | "host_tour_seen_at"
  | "earnings_tour_seen_at"
  | "reviews_tour_seen_at";

export interface UseHostTourOptions {
  /**
   * Stable per-page key used to namespace the localStorage flag. "dashboard"
   * (the default) preserves the legacy un-namespaced key. Any other key gets
   * `bac.host_tour_seen.<key>.<userId>`.
   */
  key?: string;
  /** Profile timestamp column persisted to the server for this tour. */
  profileField?: HostTourProfileField;
}

function getStorageKey(userId: string | number, key: string): string {
  return key === "dashboard"
    ? `${TOUR_STORAGE_PREFIX}${userId}`
    : `${TOUR_STORAGE_PREFIX}${key}.${userId}`;
}

function hasSeenTourLocal(userId: string | number, key: string): boolean {
  try {
    return window.localStorage.getItem(getStorageKey(userId, key)) === "1";
  } catch {
    return false;
  }
}

function markTourSeenLocal(userId: string | number, key: string): void {
  try {
    window.localStorage.setItem(getStorageKey(userId, key), "1");
  } catch {
    // silent
  }
}

function clearTourSeenLocal(userId: string | number, key: string): void {
  try {
    window.localStorage.removeItem(getStorageKey(userId, key));
  } catch {
    // silent
  }
}

function persistToServer(
  token: string,
  field: HostTourProfileField,
  timestamp: string | null,
): void {
  updateUserSettings(token, { [field]: timestamp }).catch((err) => {
    console.warn("[useHostTour] failed to persist tour flag to server", err);
  });
}

export type TourPhase = "idle" | "welcome" | "touring" | "finished";

export interface HostTourState {
  phase: TourPhase;
  stepIndex: number;
  totalSteps: number;
  showWelcome: boolean;
  showTour: boolean;
  startTour: () => void;
  skipTour: () => void;
  nextStep: () => void;
  prevStep: () => void;
  finishTour: () => void;
  dismissFinish: () => void;
  replayTour: () => void;
  hasSeen: boolean;
}

export function useHostTour(
  totalSteps: number,
  options?: UseHostTourOptions,
): HostTourState {
  const storageKey = options?.key ?? "dashboard";
  const profileField: HostTourProfileField =
    options?.profileField ?? "host_tour_seen_at";

  const { profile, session, patchProfile } = useUser();
  const userId = profile?.id;
  const token = session?.access_token;

  const [phase, setPhase] = useState<TourPhase>("idle");
  const [stepIndex, setStepIndex] = useState(0);
  const [hasSeen, setHasSeen] = useState(true);

  const initialized = useRef(false);

  const serverValue: string | null | undefined = profile?.[profileField];
  const serverAvailable = profile != null && profileField in profile;

  useEffect(() => {
    if (!userId) {
      initialized.current = false;
      return;
    }

    if (serverAvailable) {
      const serverSeen = serverValue != null;
      const localSeen = hasSeenTourLocal(userId, storageKey);

      // Treat the tour as seen when EITHER the server timestamp is set OR
      // the per-user localStorage flag is set. The local flag bridges the
      // window between a fire-and-forget dismissal PATCH and the profile
      // refetch that lands the server timestamp — without it, a remount
      // that reads a stale (still-null) profile would re-open the welcome
      // modal. The server value still wins the reconciliation below.
      const seen = serverSeen || localSeen;

      if (seen) {
        // Keep server and local in sync: when the server already knows the
        // tour was seen, persist the local flag too (for offline/stale
        // reads). We never clear the local flag here off a null server
        // value, since a not-yet-refetched profile legitimately reports
        // null right after dismissal — clearing it would re-arm the modal.
        if (serverSeen && !localSeen) markTourSeenLocal(userId, storageKey);
        setHasSeen(true);
        if (!initialized.current) {
          initialized.current = true;
        }
      } else {
        setHasSeen(false);
        if (!initialized.current) {
          setPhase("welcome");
          initialized.current = true;
        }
      }
    } else {
      if (!initialized.current) {
        const localSeen = hasSeenTourLocal(userId, storageKey);
        setHasSeen(localSeen);
        if (!localSeen) {
          setPhase("welcome");
        }
        initialized.current = true;
      }
    }
  }, [userId, serverAvailable, serverValue, storageKey]);

  const persist = useCallback(() => {
    if (!userId) return;
    const now = new Date().toISOString();
    markTourSeenLocal(userId, storageKey);
    setHasSeen(true);
    // Optimistically reflect the dismissal in the auth context so the
    // in-memory profile no longer reports `<profileField>: null`.
    // Without this, a remount that re-runs the init effect before the
    // fire-and-forget PATCH's refetch lands would read the stale null and
    // re-open the welcome modal. This makes the guard robust even if
    // localStorage is unavailable.
    patchProfile({ [profileField]: now });
    if (token) {
      persistToServer(token, profileField, now);
    }
  }, [userId, token, patchProfile, storageKey, profileField]);

  const startTour = useCallback(() => {
    setStepIndex(0);
    setPhase("touring");
  }, []);

  const skipTour = useCallback(() => {
    setPhase("idle");
    persist();
  }, [persist]);

  const nextStep = useCallback(() => {
    setStepIndex((prev) => {
      if (prev >= totalSteps - 1) {
        setPhase("finished");
        persist();
        return prev;
      }
      return prev + 1;
    });
  }, [totalSteps, persist]);

  const prevStep = useCallback(() => {
    setStepIndex((prev) => Math.max(0, prev - 1));
  }, []);

  const finishTour = useCallback(() => {
    setPhase("finished");
    persist();
  }, [persist]);

  const dismissFinish = useCallback(() => {
    setPhase("idle");
  }, []);

  const replayTour = useCallback(() => {
    if (userId) {
      clearTourSeenLocal(userId, storageKey);
      setHasSeen(false);
    }
    // Mirror the cleared state into the in-memory profile so a remount's
    // init effect (which now treats a non-null server value OR local flag
    // as "seen") doesn't immediately re-suppress the replayed welcome.
    patchProfile({ [profileField]: null });
    if (token) {
      persistToServer(token, profileField, null);
    }
    setStepIndex(0);
    setPhase("welcome");
  }, [userId, token, patchProfile, storageKey, profileField]);

  return {
    phase,
    stepIndex,
    totalSteps,
    showWelcome: phase === "welcome",
    showTour: phase === "touring",
    startTour,
    skipTour,
    nextStep,
    prevStep,
    finishTour,
    dismissFinish,
    replayTour,
    hasSeen,
  };
}
